<template>
  <div :class="['chat-header', isH5 && 'chat-header-h5']">
    <div class="chat-header-name">
      <div
        v-if="isH5 && props.canCloseChat"
        :class="['chat-header-back', isH5 && 'chat-header-h5-back']"
        @click="closeChat(currentConversation.conversationID)"
      >
        <Icon :file="backSVG" />
      </div>
      <div class="chat-header-container">
        <Icon v-if="showHeaderIcon" :width="headerIconWidth" :height="headerIconHeight" :file="headerIcon" />
        <div :class="['chat-header-content', isH5 && 'chat-header-h5-content']">
          {{ currentConversationName }}
        </div>
      </div>
    </div>
    <div v-if="props.enableMultilingual === 1" class="language-selector">
      <div class="selected-language-option" @click="toggleDropdown">
        {{ currentLanguageOption.name }}
        <span class="arrow" :class="{ 'arrow-up': showDropdown }">
          <Icon :file="arrowDown" width="12px" height="12px" />
        </span>
      </div>
      
      <transition name="fade">
        <div v-if="showDropdown" class="dropdown-popup">
          <div 
            v-for="option in languageOptionList" 
            :key="option.code"
            :class="['dropdown-item',currentLanguageOption.code === option.code ? 'dropdown-item-selected' : '']"
            @click="selectLanguage(option)"
          >
            <div class="language">
              <div class="language-code">
                {{ option.codeForShow }}
              </div>
              <div class="language-name">
                {{ option.name }}
              </div>
            </div>
            
            <Icon v-if="currentLanguageOption.code === option.code" :file="languageSelectedIcon" width="20px" height="20px" />
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>
<script lang="ts" setup>
import vue from '../../../adapter-vue';
import {
  TUIStore,
  StoreName,
  TUITranslateService,
  IConversationModel,
} from '../../../@aidesk/uikit-engine';
import Icon from '../../common/Icon.vue';
import backSVG from '../../../assets/back.svg';
import arrowDown from '../../../assets/language_arrow_down.svg';
import customerAvatarMobile from '../../../assets/customer_avatar_mobile.png';
import languageSelectedIcon from '../../../assets/language_check.svg';
import { isH5 } from '../../../utils/env';
import state from '../../../utils/state';
import { SUPPORTED_LANGUAGES } from '../../../constant';
import { IHeaderConfig } from '../../../interface';
const { ref, onMounted, onUnmounted, computed } = vue;

interface IProps {
  canCloseChat?: boolean;
  currentLang?: string;
  enableMultilingual: number;
  langList?: Array<string>;
  headerConfig?: IHeaderConfig;
}
interface ILanguage {
  name: string;
  code: string;
  codeForShow: string;
}

const emits = defineEmits(['closeChat','changeLanguage']);
const currentConversation = ref<IConversationModel>();
const currentConversationName = ref('');
const isTyping = ref(false);
const showDropdown = ref(false);
const languageOptionList = ref<Array<ILanguage>>([]);
const currentLanguageOption = ref<ILanguage>({name:'',code:'',codeForShow:''});
 
const props = withDefaults(defineProps<IProps>(), {
  canCloseChat: true,
  currentLang: '',
  enableMultilingual: 0,
  langList: () => [],
});

const showHeaderIcon = computed(() => {
  if (isH5) {
    if (props.headerConfig && props.headerConfig.showLogo === false) {
      return false;
    }
    return true;
  }
  return false;
});

const headerIcon = computed(() => {
  return (props.headerConfig && props.headerConfig.logo) || customerAvatarMobile;
});

const headerIconWidth = computed(() => {
  return (props.headerConfig && props.headerConfig.logoWidth) ?? '32px';
});

const headerIconHeight = computed(() => {
  if (props.headerConfig) {
    return props.headerConfig.logoHeight || '32px';
  }
  return '23px';
});

if (props.enableMultilingual === 1) {
  languageOptionList.value = props.langList
  .map(key => ({
    code: key,
    codeForShow: SUPPORTED_LANGUAGES[key].codeForShow,
    name: SUPPORTED_LANGUAGES[key].name,
  }));
  currentLanguageOption.value = languageOptionList.value.find(option => option.code === props.currentLang);
}

onMounted(() => {
  TUIStore.watch(StoreName.CONV, {
    currentConversation: onCurrentConversationUpdated,
  });

  TUIStore.watch(StoreName.CUSTOM, {
    isTyping: onTypingStatusUpdated,
  });
});

onUnmounted(() => {
  TUIStore.unwatch(StoreName.CONV, {
    currentConversation: onCurrentConversationUpdated,
  });

  TUIStore.unwatch(StoreName.CUSTOM, {
    isTyping: onTypingStatusUpdated,
  });
});

const closeChat = (conversationID: string | undefined) => {
  emits('closeChat', conversationID);
};

function setCurrentConversationName() {
  if (!currentConversation.value) {
    return;
  }
  if (props.headerConfig && props.headerConfig.title) {
    currentConversationName.value = props.headerConfig.title;
  } else {
    currentConversationName.value = currentConversation.value.getShowName() || '';
  }
}

function onCurrentConversationUpdated(conversation: IConversationModel) {
  currentConversation.value = conversation;
  setCurrentConversationName();
}

function onTypingStatusUpdated(data: {conversationID: string, value: boolean}) {
  if (data && data.conversationID === currentConversation.value.conversationID) {
    isTyping.value = data.value;
    if (isTyping.value && state.get('showTyping') === 1) {
      currentConversationName.value = TUITranslateService.t('TUIChat.对方正在输入');
    } else {
      setCurrentConversationName();
    }
  }
}

function toggleDropdown() {
  showDropdown.value = !showDropdown.value
}

function selectLanguage(languageOption) {
  showDropdown.value = false;
  currentLanguageOption.value = languageOption;
  emits('changeLanguage', languageOption.code);
}
</script>
<style lang="scss" scoped>
.chat-header-name {
  display: flex;
  flex-direction: row;
  flex: 1;
  min-width: 0;
}
.chat-header {
  display: flex;
  min-width: 0;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-bottom:1px solid #f3f4f7;
  width: 100%;

  &-container {
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: row;
    justify-content: flex-start;
  }

  &-content {
    margin-right: 20px;
    flex: 1;
    font-size: 16px;
    line-height: 30px;
    font-weight: 500;
    color: #000;
    letter-spacing: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    min-width: 0;
  }

  &-back,
  &-setting {
    width: 27px;
    height: 27px;

    .icon {
      width: 100%;
      height: 100%;
    }
  }
}

.chat-header-h5 {
  border:none;
  justify-content: flex-start;
  &-back {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &-content {
    margin: 0 20px 0 10px;
  }
}

.language-selector {
  position: relative;
  cursor: pointer;
  user-select: none;
  flex-shrink: 0;
}

.selected-language-option {
  padding: 8px 6px;
  border-radius: 4px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  font-size: 12px;
  color: #0F1014;
  gap: 5px;
}

.arrow {
  transition: transform 0.2s;
  font-size: 12px;
}

.arrow-up {
  transform: rotate(180deg);
}

.dropdown-popup {
  position: absolute;
  top: 100%;
  right: 10px;
  font-size: 12px;
  max-width: 200px;
  max-height: 400px;
  border-radius: 8px;
  background-color: white;
  box-shadow: 0px 1px 8px 0px var(---Black-8, rgba(0, 0, 0, 0.06)), 0px 4px 12px 0px var(---Black-8, rgba(0, 0, 0, 0.06)), 0px 10px 30px 0px var(---Black-8, rgba(0, 0, 0, 0.06));
  z-index: 1000;
  margin-top: 4px;
  padding: 8px 0px;
  overflow-y: auto;
  scrollbar-width: 4px;
  scrollbar-color: #E7EAEF;
}

.dropdown-popup::-webkit-scrollbar {
  width: 4px;
}
.dropdown-popup::-webkit-scrollbar-track {
  background: transparent;
}
.dropdown-popup::-webkit-scrollbar-thumb {
  background-color: #E7EAEF;
  border-radius: 2px;
}

.dropdown-item {
  transition: background-color 0.2s;
  display: flex;
  flex-direction: row;
  padding: 5px 12px;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  .language {
    display: flex;
    gap: 8px;
    align-items: center;
    flex: 1;
    min-width: 0;
    font-size: 12px;
    .language-code {
      color: #71798A;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 2px;
      border: 1px solid rgba(114, 122, 138, 0.20);
      padding: 0 8px;
      font-weight: 600;
      min-width: 12px;
      flex-shrink: 0;
    }
    .language-name {
      color: #0F1014;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      flex: 1;
      min-width: 0; 
    }
  }
}

.dropdown-item:hover,.dropdown-item-selected {
  background-color: #F2F6FF;
}

.dropdown-item-selected {
  position: relative;
}

.dropdown-item-selected::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: #F2F6FF;
  z-index: -1;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
